import type { ErrorEvent, EventHint, User } from '@sentry/react';

import { ApiErrorResponse, isApiErrorResponse } from 'types/Error';
import type { MayBe } from 'types/common';

const RESOURCE_TAGS = /^(LINK|SCRIPT|IMG|SOURCE|AUDIO|VIDEO)$/;

const CLIENT_ERROR_MIN = 400;
const SERVER_ERROR_MIN = 500;

/**
 * Detects a DOM resource-loading failure (a `<link>`, `<script>`, `<img>`, ...
 * that failed to load) surfaced as a generic `Event` rather than a real JS
 * `Error`. These are CDN/network failures, not actionable exceptions, and only
 * pollute Sentry with ungroupable "Event captured as promise rejection" noise.
 */
export const isResourceLoadEvent = (originalException: unknown): boolean => {
  if (typeof Event === 'undefined' || !(originalException instanceof Event)) {
    return false;
  }

  const target = originalException.target as HTMLElement | null;

  return Boolean(target?.tagName && RESOURCE_TAGS.test(target.tagName));
};

/**
 * `Sentry.init` `beforeSend` hook: drops non-actionable resource-load events
 * before they are sent. Returning `null` discards the event.
 */
export const beforeSend = (event: ErrorEvent, hint: EventHint): ErrorEvent | null => {
  if (isResourceLoadEvent(hint?.originalException)) {
    return null;
  }

  return event;
};

/**
 * Detects an `AbortError` — typically an RTK Query request cancelled in-flight
 * when its query is unmounted or superseded. Benign and expected; capturing it
 * only pollutes Sentry. Handles both the top-level shape and the
 * `{ error: SerializedError }` shape returned by `queryFulfilled` rejections.
 */
export const isAbortError = (error: unknown): boolean => {
  const candidate = error as MayBe<{ name?: string; error?: { name?: string } }>;

  return candidate?.name === 'AbortError' || candidate?.error?.name === 'AbortError';
};

/**
 * Detects an expected client error (HTTP 4xx) coming from the API. These are
 * business/operational outcomes (e.g. "key is not in the slot") already handled
 * by the UI, not JS bugs, so they should not be reported as Sentry errors.
 */
export const isExpectedClientError = (error: unknown): boolean => (
  isApiErrorResponse(error as ApiErrorResponse)
    && (error as ApiErrorResponse).status >= CLIENT_ERROR_MIN
    && (error as ApiErrorResponse).status < SERVER_ERROR_MIN
);

/**
 * Normalizes a raw API error object into a real `Error` so Sentry groups it by
 * message instead of the unhelpful "Object captured as exception with keys: …".
 * Non-API errors are returned untouched.
 */
export const toReportableError = (error: unknown): unknown => {
  if (!isApiErrorResponse(error as ApiErrorResponse)) {
    return error;
  }

  const { status, data } = error as ApiErrorResponse;
  const reportable = new Error(data?.errorMessage || `API error ${status}`);
  reportable.name = `ApiError(${status})`;

  return reportable;
};

type SentryUserInput = {
  publicToken: MayBe<string>;
  selfServiceId?: MayBe<string>;
  dealerId?: MayBe<string>;
};

/**
 * Builds the Sentry user from the self-service session identity. The public
 * token is the stable session identifier (also tracked as a tag); the optional
 * UUIDs help triage by dealer / self-service. Returns `null` when no token is
 * known, which clears the Sentry user.
 */
export const buildSentryUser = ({ publicToken, selfServiceId, dealerId }: SentryUserInput): User | null => {
  if (!publicToken) {
    return null;
  }

  return {
    id: publicToken,
    ...(selfServiceId ? { selfServiceId } : {}),
    ...(dealerId ? { dealerId } : {}),
  };
};
